import type { Http } from '../http';
import { endpoints } from '../endpoints';
import type {
  RawLoginRequest,
  RawLoginResponse,
  RawRefreshRequest,
  RawRefreshResponse,
  RawLogoutRequest,
  RawLogoutResponse,
  RawFcmRegisterRequest,
  RawFcmRegisterResponse,
} from '../types/auth';

export function createAuthApi(http: Http) {
  return {
    async login(data: RawLoginRequest): Promise<RawLoginResponse> {
      const res = await http.post<RawLoginResponse>(endpoints.auth.login, data);
      return res.data;
    },

    /**
     * access 재발급. `refreshToken` 을 주면 그 값을 `Authorization: Bearer` 로 직접 싣는다 —
     * refresh 엔드포인트는 access 가 아닌 refresh 토큰으로 인증하기 때문(BE refresh-binding). 헤더 주입을
     * 호출처(composition 의 공통 refreshToken)로 승격해, transport 는 refresh 를 특별 취급하지 않는다.
     */
    async refreshToken(
      data: RawRefreshRequest,
      refreshToken?: string | null,
    ): Promise<RawRefreshResponse> {
      const res = await http.post<RawRefreshResponse>(endpoints.auth.refresh, data, {
        headers: refreshToken ? { Authorization: `Bearer ${refreshToken}` } : undefined,
      });
      return res.data;
    },

    /**
     * 로그아웃. `accessToken` 을 주면 그 스냅샷을 Authorization 헤더에 직접 싣는다 — caller 가
     * 로컬 세션을 즉시 비운 뒤(best-effort teardown) 호출해도 요청이 유효 토큰을 유지하도록.
     * 생략 시 transport 인터셉터의 store 기반 기본 토큰을 사용한다.
     */
    async logout(data: RawLogoutRequest, accessToken?: string | null): Promise<RawLogoutResponse> {
      const res = await http.post<RawLogoutResponse>(endpoints.auth.logout, data, {
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
      });
      return res.data;
    },

    /** 디바이스 FCM 토큰 등록. access token 헤더 자동 부여(인증 필요). */
    async registerFcm(data: RawFcmRegisterRequest): Promise<RawFcmRegisterResponse> {
      const res = await http.post<RawFcmRegisterResponse>(endpoints.auth.fcm, data);
      return res.data;
    },
  };
}
